<script setup>
import {gameColumnPossible} from '~/utils/setGrid';
import {computed, onMounted, watch} from 'vue';
import TeamCard from "~/components/teams/TeamCard.vue";
import _ from "lodash";
import ScoringSystemPopup from "~/components/fields/ScoringSystemPopup.vue";
import HandicapMatchBanner from "~/components/handicaps/HandicapMatchBanner.vue";
import {parseScoreSchema} from "~/utils/scoring";
// Define props
const props = defineProps({
  homeTeam: Object,
  awayTeam: Object,
  // context already names the system (e.g. the preview dialog title) — skip the banner
  hideSystemBanner: {
    type: Boolean,
    default: false,
  },
  // Americano + split-games: the set grid is the only full entry mode — the
  // "Overall score" shortcut is hidden and only walkover outcomes are pickable
  // without rows (mirrors the API's set-score enforcement).
  requireGameScores: {
    type: Boolean,
    default: false,
  },
  // 1-based game slot that is the champions tiebreak — subtly differentiated
  // in the grid so it doesn't read as just another set.
  tiebreakGameNumber: {
    type: Number,
    default: null,
  },
  // View-only dialogs must not offer the goal-scorer editor — the rows could
  // never be saved (the fixture page's Goals card is the read surface).
  readOnly: {
    type: Boolean,
    default: false,
  },
  // The competition's customised per-outcome points (fixture.scoringOverrides
  // from the API) so the popup shows the EFFECTIVE values, not the defaults.
  // FixtureDetailResponse.handicapStart — the resolved start for THIS match. Drives the banner,
  // per-cell placeholders (only when the typed-in unit is the one the start sits on) and
  // whether a minus sign is allowed (a side starting below zero can finish negative).
  handicapStart: {
    type: Object,
    default: null,
  },
  overrides: {
    type: Array,
    default: () => [],
  },
});
const scoringSystem = defineModel("scoringSystem", {type: Object, required: true});
const homeResult = defineModel("homeResult", {type: String});
const awayResult = defineModel("awayResult", {type: String});
const games = defineModel("games", {type: Array});
// Goal scorers (football): [{side: 'home'|'away', playerId, minute, ownGoal}].
// The section only renders when the schema declares a player-required scoring
// event ("goal"), so every other sport is untouched.
const scorers = defineModel("scorers", {type: Array, default: () => []});
const dialog = ref(false);

// Snapshot of the SYSTEM's capability (clicking the toggle mutates the local
// model's splitGames, so this must not be reactive to it) — the toggle itself
// disappears when set scores are required.
const allowSplitGames = ref(!!(scoringSystem.value && scoringSystem.value.splitGames));
const showModeToggle = computed(() => allowSplitGames.value && !props.requireGameScores);

// Walkover affordance for enforced-set entry: swaps the grid for the overall
// pickers restricted to outcomes with no games split (WO/FF).
const walkoverMode = ref(false);
const toggleWalkoverMode = () => {
  walkoverMode.value = !walkoverMode.value;
  homeResult.value = null;
  awayResult.value = null;
  // Entering walkover mode abandons any part-typed grid — stale rows must not
  // ride along on the walkover request.
  if (walkoverMode.value) {
    games.value.forEach(g => {
      g.homeTeamScore = null;
      g.awayTeamScore = null;
    });
  } else if (scoringSystem.value) {
    scoringSystem.value.splitGames = true;
  }
};
const showGrid = computed(() =>
    scoringSystem.value != null && scoringSystem.value.splitGames &&
    !(props.requireGameScores && walkoverMode.value));
const showOverallPickers = computed(() =>
    scoringSystem.value != null && !scoringSystem.value.splitGames ||
    (props.requireGameScores && walkoverMode.value));

// Enforced mode pins the grid on, even when a rowless historic result reopened.
watch(() => props.requireGameScores, (required) => {
  if (required && scoringSystem.value) scoringSystem.value.splitGames = true;
}, {immediate: true});

const isTiebreakColumn = (i) => props.tiebreakGameNumber != null && i + 1 === props.tiebreakGameNumber;

// What one "game" is actually called for this sport, from the scoring system's schema
// (Leg for darts, Set for tennis, Frame for snooker, End for bowls...). Systems without
// a schema (legacy fallback) keep the generic "Game".
const gameUnitLabel = computed(() => {
  try {
    if (!scoringSystem.value?.schema) return 'Game';
    const parsed = parseScoreSchema(scoringSystem.value.schema, scoringSystem.value.schemaParamValues);
    const gamesFrom = parsed.resultMapping?.gamesFrom;
    return (gamesFrom && parsed.units?.[gamesFrom]?.label) || 'Game';
  } catch {
    return 'Game';
  }
});

// Computed property to check if both teams are available
const bothTeamsPresent = computed(() => !!props.homeTeam?.id && !!props.awayTeam?.id);

// ── Goal scorers (schema-driven; football-standard today) ──
const parsedSchema = computed(() => {
  try {
    if (!scoringSystem.value?.schema) return null;
    return parseScoreSchema(scoringSystem.value.schema, scoringSystem.value.schemaParamValues);
  } catch {
    return null;
  }
});
const goalEvent = computed(() =>
    (parsedSchema.value?.events ?? []).find(e => e.playerRequired && e.affectsScore) ?? null);
const hasOwnGoal = computed(() =>
    (parsedSchema.value?.events ?? []).some(e => e.key === 'ownGoal'));
// Scorers only exist in game-scores mode — the goals ARE the game score.
// Switching to Overall (W/D/L) hides the table; the rows themselves are
// cleared by the mode watcher below so a stale scorer can't ride along.
const showScorers = computed(() =>
    goalEvent.value != null && bothTeamsPresent.value && !props.readOnly && showGrid.value);
watch(showGrid, (grid) => {
  if (!grid && scorers.value.length) scorers.value = [];
});

const playerLabel = (p) => p.fullName || [p.firstName, p.lastName].filter(Boolean).join(' ') || 'Player';
// A row credited to one side is scored by that side's player — unless it's an
// own goal, which the OTHER side's player put in.
const scorerPlayerItems = (row) => {
  const homeSide = row.side === 'home';
  const scoringTeam = (homeSide !== !!row.ownGoal) ? props.homeTeam : props.awayTeam;
  return scoringTeam?.players ?? [];
};
const sideScorers = (side) => scorers.value.filter(r => r.side === side);

// One picker per goal, driven by the entered score — nothing to add or
// remove, no minutes: each row is just "who scored goal N?". Own goals live
// inside the same select (the opponent's players, marked), so there's no
// checkbox to decode.
const scorerOptions = (side) => {
  const mine = side === 'home' ? props.homeTeam : props.awayTeam;
  const other = side === 'home' ? props.awayTeam : props.homeTeam;
  // Not-recorded first (it's the default and the quick out), then the team's
  // own scorers, own goals last — they're the LEAST likely pick.
  return [
    {value: null, title: 'Scorer not recorded'},
    ...(mine?.players ?? []).map(p => ({value: 'p:' + p.id, title: playerLabel(p)})),
    ...(hasOwnGoal.value
        ? (other?.players ?? []).map(p => ({value: 'og:' + p.id, title: `${playerLabel(p)} (own goal)`}))
        : []),
  ];
};
const scorerRowCount = computed(() =>
    Math.max(sideScorers('home').length, sideScorers('away').length));
const rowValue = (row) => (row.playerId == null ? null : (row.ownGoal ? 'og:' : 'p:') + row.playerId);
const setRowValue = (row, v) => {
  if (v == null) {
    row.playerId = null;
    row.ownGoal = false;
    return;
  }
  row.ownGoal = v.startsWith('og:');
  row.playerId = v.slice(v.indexOf(':') + 1);
};

// Keep exactly score-many rows per side (drop empty rows first when the score
// shrinks; the score is authoritative over loaded rows).
const syncScorerRows = () => {
  if (!showScorers.value) return;
  for (const side of ['home', 'away']) {
    const want = goalsEntered(side) ?? 0;
    const rows = sideScorers(side);
    if (rows.length < want) {
      scorers.value = [...scorers.value,
        ...Array.from({length: want - rows.length}, () => ({side, playerId: null, minute: null, ownGoal: false}))];
    } else if (rows.length > want) {
      let toDrop = rows.length - want;
      const keep = [];
      for (const r of [...rows].reverse()) {
        if (toDrop > 0 && r.playerId == null) {
          toDrop--;
          continue;
        }
        keep.unshift(r);
      }
      while (toDrop > 0 && keep.length) {
        keep.pop();
        toDrop--;
      }
      scorers.value = [...scorers.value.filter(r => r.side !== side), ...keep];
    }
  }
};
const goalsEntered = (side) => {
  const game = games.value?.[0];
  const score = side === 'home' ? game?.homeTeamScore : game?.awayTeamScore;
  const n = parseInt(score);
  return isNaN(n) ? null : n;
};
watch([games, () => showScorers.value, scorers], syncScorerRows, {deep: true, immediate: true});
// Helper function to determine if a game is possible
// ── Handicap hints (server-resolved; nothing sport-specific here) ──
const entryHint = computed(() => props.handicapStart?.entryHint ?? null);
const allowNegative = computed(() => entryHint.value?.allowNegative === true);
const handicapPlaceholder = (i, side) => {
  const hint = entryHint.value;
  if (!hint) return undefined;
  const idx = side === 'home' ? 0 : 1;
  let start = hint.starts?.[idx] ?? 0;
  const p = hint.pattern;
  if (p && p.everyN > 0 && (i % p.everyN) < p.extraInFirstK) start += p.extra?.[idx] ?? 0;
  return `${start}`;
};

// X202 (R82 L11): a decided match opens no further column (utils/setGrid).
const gamePossible = (gameNumber) => gameColumnPossible(games.value, scoringSystem.value?.outcomes, gameNumber);

// Tab walks the scorecard game by game (1 home, 1 away, 2 home, 2 away...) even though
// the DOM is row-major; Shift+Tab reverses. At the grid's edges the browser default takes
// over, and disabled (not-yet-possible) games fall through naturally.
const onGameTab = (event, i, side) => {
  const forward = !event.shiftKey;
  const [ti, tSide] = forward
      ? (side === 'home' ? [i, 'away'] : [i + 1, 'home'])
      : (side === 'away' ? [i, 'home'] : [i - 1, 'away']);
  if (ti < 0 || ti >= games.value.length) return;
  const el = document.getElementById(`${scoringSystem.value.id}-game-${ti}-${tSide}`);
  if (el && !el.disabled) {
    event.preventDefault();
    el.focus();
  }
};

const homeOptions = computed(() => {
  if (!scoringSystem.value) return [];
  let outcomes = _.clone(scoringSystem.value.outcomes);
  if (props.requireGameScores && walkoverMode.value) {
    outcomes = outcomes.filter(o => o.homeGames == null);
  }
  return _.uniqBy(outcomes.sort((a, b) => (a.homeSort > b.homeSort) ? 1 : -1), 'homeResult');
});

const awayOptions = computed(() => {
  if (!scoringSystem.value) return [];
  let outcomes = _.clone(scoringSystem.value.outcomes);
  if (props.requireGameScores && walkoverMode.value) {
    outcomes = outcomes.filter(o => o.homeGames == null);
  }
  if (homeResult.value != null) {
    outcomes = outcomes.filter(o => o.homeResult === homeResult.value);
  }
  return _.uniqBy(outcomes.sort((a, b) => (a.awaySort > b.awaySort) ? 1 : -1), 'awayResult');
});

</script>
<template>
  <div>
    <!-- same segmented control as the Matrix/Standings and bracket tabs (tl-seg, ink fill),
         stretched full width for this form context -->
    <div v-if="scoringSystem && showModeToggle" class="mb-4">
      <div class="tl-label mb-2">Scoring input type</div>
      <div class="tl-seg ssi-seg">
        <button
            type="button"
            class="tl-seg__opt"
            :class="{'tl-seg__opt--active': !scoringSystem.splitGames}"
            @click="scoringSystem.splitGames = false"
        >Overall score</button>
        <button
            type="button"
            class="tl-seg__opt"
            :class="{'tl-seg__opt--active': scoringSystem.splitGames}"
            @click="scoringSystem.splitGames = true"
        >{{ gameUnitLabel }} scores</button>
      </div>
    </div>
    <div v-if="requireGameScores" class="ssi-required-note mb-3">
      <v-icon size="14" icon="mdi-format-list-numbered"/>
      <span v-if="!walkoverMode">
        Full {{ gameUnitLabel.toLowerCase() }} scores are required for this americano.
        <a class="ssi-link" @click.prevent="toggleWalkoverMode">Record a walkover instead</a>
      </span>
      <span v-else>
        Recording a walkover / forfeit.
        <a class="ssi-link" @click.prevent="toggleWalkoverMode">Back to {{ gameUnitLabel.toLowerCase() }} scores</a>
      </span>
    </div>
    <table style="width: 100%">
      <thead v-if="bothTeamsPresent">
      <tr v-if="scoringSystem && !hideSystemBanner">
        <td colspan="2" style="width: 50%; vertical-align: top;" class="text-center">
          <div class="d-flex justify-between align-center pa-4 mb-4 font-weight-bold" style="background: rgb(var(--v-theme-light)); border:1px solid rgb(var(--v-theme-borderLight)); border-radius:10px;">
            <v-icon icon="mdi-scoreboard" color="primary" class="mr-2"></v-icon>
            <div class="flex-fill text-start">Scoring system : {{ scoringSystem.name }}</div>
            <scoring-system-popup :allow-split-games="allowSplitGames" :scoring-system="scoringSystem" :overrides="overrides"></scoring-system-popup>
          </div>
        </td>
      </tr>
      <tr v-if="showOverallPickers">
        <td style="width: 50%; vertical-align: top;" class="text-center">
          <div class="d-flex justify-center pb-4">
            <team-card size="small" :team="homeTeam"></team-card>
          </div>
        </td>
        <td style="width: 50%; vertical-align: top;" class="text-center">
          <div class="d-flex justify-center pb-4 flex-column align-center">
            <team-card size="small" :team="awayTeam"></team-card>
          </div>
        </td>
      </tr>
      </thead>
      <template v-if="showGrid">
        <!-- horizontal scorecard: one row per team, one column per possible game; games
             that can't happen yet are greyed out rather than hidden -->
        <tr>
          <td colspan="2" class="pa-0">
            <div class="ssi-scorecard-wrap">
              <table class="ssi-scorecard">
                <thead>
                  <!-- one merged unit-name header ("Games" / "Sets" / "Legs"...) over
                       numbered columns; the total column is the match result -->
                  <tr>
                    <th rowspan="2"></th>
                    <th :colspan="games.length" class="ssi-group-h">{{ gameUnitLabel }}s</th>
                    <th rowspan="2" class="ssi-gap"></th>
                    <th rowspan="2" class="ssi-total-h">Result</th>
                  </tr>
                  <tr>
                    <th v-for="(game, i) in games" :key="'h'+i" class="ssi-num-h"
                        :class="{'ssi-col--off': !gamePossible(i), 'ssi-col--tb': isTiebreakColumn(i)}">
                      <v-tooltip v-if="isTiebreakColumn(i)" text="Champions tiebreak" location="top">
                        <template #activator="{ props: tbTip }">
                          <span v-bind="tbTip">TB</span>
                        </template>
                      </v-tooltip>
                      <template v-else>{{ i + 1 }}</template>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="ssi-team">
                      <team-card size="small" :team="homeTeam" :max-lines="1" :show-squash-level="false"/>
                    </td>
                    <td v-for="(game, i) in games" :key="scoringSystem.id+'-game-'+i+'-home'"
                        class="ssi-cell" :class="{'ssi-cell--tb': isTiebreakColumn(i)}">
                      <v-text-field
                          step="1"
                          :min="allowNegative ? undefined : 0"
                          :placeholder="handicapPlaceholder(i, 'home')"
                          :id="scoringSystem.id+'-game-'+i+'-home'"
                          onkeydown="if(event.key === '.'){event.preventDefault();}"
                          type="number"
                          density="compact"
                          hide-details
                          autocomplete="false"
                          :disabled="!gamePossible(i)"
                          v-model="games[i].homeTeamScore"
                          @keydown.tab="onGameTab($event, i, 'home')"
                      ></v-text-field>
                    </td>
                    <td class="ssi-gap"></td>
                    <td class="ssi-total">{{ homeResult != null ? homeResult : '0' }}</td>
                  </tr>
                  <tr>
                    <td class="ssi-team">
                      <team-card size="small" :team="awayTeam" :max-lines="1" :show-squash-level="false"/>
                    </td>
                    <td v-for="(game, i) in games" :key="scoringSystem.id+'-game-'+i+'-away'"
                        class="ssi-cell" :class="{'ssi-cell--tb': isTiebreakColumn(i)}">
                      <v-text-field
                          step="1"
                          :min="allowNegative ? undefined : 0"
                          :placeholder="handicapPlaceholder(i, 'away')"
                          :id="scoringSystem.id+'-game-'+i+'-away'"
                          onkeydown="if(event.key === '.'){event.preventDefault();}"
                          type="number"
                          density="compact"
                          hide-details
                          autocomplete="false"
                          :disabled="!gamePossible(i)"
                          v-model="games[i].awayTeamScore"
                          @keydown.tab="onGameTab($event, i, 'away')"
                      ></v-text-field>
                    </td>
                    <td class="ssi-gap"></td>
                    <td class="ssi-total">{{ awayResult != null ? awayResult : '0' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      </template>
      <template v-else-if="showOverallPickers">
        <tr :key="'simpleScore'+scoringSystem.id">
          <td class="py-1 px-2">
            <v-select
                :items="homeOptions"
                item-value="homeResult"
                item-title="homeResult"
                :placeholder="'Match result'"
                persistent-hint
                autocomplete="false"
                v-model="homeResult"
                label="Score"
                :persistent-placeholder="true"
            ></v-select>
          </td>
          <td class="py-1 px-2">
            <v-select
                :placeholder="'Match result'"
                persistent-hint
                :items="awayOptions"
                item-value="awayResult"
                item-title="awayResult"
                autocomplete="false"
                v-model="awayResult"
                label="Score"
                :persistent-placeholder="true"
            ></v-select>
          </td>
        </tr>
        <tr>
          <td class="text-center px-2">
            <div class="pa-3" style="background: rgb(var(--v-theme-light)); border:1px solid rgb(var(--v-theme-borderLight)); border-radius:10px;">
              <h1>{{ homeResult != null ? homeResult : '-' }}</h1>
            </div>
          </td>
          <td class="text-center px-2">
            <div class="pa-3" style="background: rgb(var(--v-theme-light)); border:1px solid rgb(var(--v-theme-borderLight)); border-radius:10px;">
              <h1>{{ awayResult != null ? awayResult : '-' }}</h1>
            </div>
          </td>
        </tr>
      </template>
    </table>

    <!-- The resolved handicap for this match — BELOW the score entry, so it is read before
         the dialog is scrolled (Grant, 2026-09-06): people enter the score and may not scroll -->
    <handicap-match-banner v-if="handicapStart && !handicapStart.isLevel" :start="handicapStart" class="mt-3"/>

    <!-- ── Goal scorers (football): one picker per goal, driven by the score ── -->
    <div v-if="showScorers" class="ssi-scorers mt-4">
      <div class="tl-label mb-2">Goal scorers</div>
      <div v-if="scorerRowCount === 0" class="text-caption text-medium-emphasis">
        Enter the score above and a row appears for each goal.
      </div>
      <table v-else class="ssi-scorers-table">
        <thead>
          <tr>
            <th class="ssi-scorers-th ssi-scorers-th--num">Goal</th>
            <th class="ssi-scorers-th">{{ homeTeam?.name || 'Home' }}</th>
            <th class="ssi-scorers-th">{{ awayTeam?.name || 'Away' }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="i in scorerRowCount" :key="i">
            <td class="ssi-scorers-td ssi-scorers-td--num">{{ i }}</td>
            <td v-for="side in ['home', 'away']" :key="side" class="ssi-scorers-td">
              <v-select
                  v-if="sideScorers(side)[i - 1]"
                  :model-value="rowValue(sideScorers(side)[i - 1])"
                  :items="scorerOptions(side)"
                  density="compact"
                  hide-details
                  placeholder="Scorer not recorded"
                  class="ssi-scorer-select"
                  :menu-props="{contentClass: 'ssi-scorer-menu'}"
                  @update:model-value="setRowValue(sideScorers(side)[i - 1], $event)"
              />
              <span v-else class="ssi-scorers-dash">–</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.ssi-scorers-table {
  width: 100%;
  border-collapse: collapse;
}

.ssi-scorers-th,
.ssi-scorers-td {
  border: 1px solid rgb(var(--v-theme-borderLight));
  padding: 6px 8px;
  text-align: left;
}

.ssi-scorers-th {
  font-size: 12px;
  font-weight: 700;
  background: rgb(var(--v-theme-light));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 0;
}

.ssi-scorers-th--num,
.ssi-scorers-td--num {
  width: 48px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: rgb(var(--v-theme-textSubtle));
}

.ssi-scorers-dash {
  color: rgb(var(--v-theme-textSubtle));
}

/* Deliberate one-off exception to the 16px input rule: a 5v5 scoresheet packs
   ten pickers into one dialog, so these run compact (Grant, 26 Aug 2026). */
.ssi-scorer-select :deep(.v-field__input) {
  min-height: 28px;
  padding-top: 0;
  padding-bottom: 0;
  font-size: 12px;
}

/* the selection renders in its own span — the field font alone doesn't shrink it */
.ssi-scorer-select :deep(.v-select__selection-text),
.ssi-scorer-select :deep(.v-field__input input::placeholder) {
  font-size: 12px;
}

.ssi-scorer-select :deep(.v-field) {
  --v-field-padding-start: 8px;
  --v-field-padding-end: 4px;
  align-items: center;
}

/* keep the open/close chevron centred on the shortened field */
.ssi-scorer-select :deep(.v-field__append-inner) {
  padding-top: 0;
  align-items: center;
}

/* full-width variant of the shared segmented control for this form */
.ssi-seg {
  width: 100%;
}
.ssi-seg .tl-seg__opt {
  flex: 1 1 0;
}

/* horizontal scorecard: team rows, game columns; narrow screens scroll sideways */
.ssi-scorecard-wrap {
  overflow-x: auto;
}
.ssi-scorecard {
  width: 100%;
  border-collapse: collapse;
}
.ssi-scorecard th {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-surface), 0.55);
  text-align: center;
  white-space: nowrap;
  vertical-align: bottom;
}
/* merged unit-name header over the numbered columns */
.ssi-group-h {
  padding: 0 4px 2px;
}
.ssi-num-h {
  font-size: 10.5px;
  padding: 0 4px 6px;
}
.ssi-scorecard th.ssi-col--off {
  opacity: 0.35;
}
.ssi-scorecard td {
  padding: 5px 4px;
}
/* the team column absorbs the table's spare width so the game and Result columns
   stay content-sized */
.ssi-team {
  width: 100%;
  min-width: 110px;
  padding-right: 10px !important;
}
/* every game column identical: same fixed width, same padding */
.ssi-cell {
  min-width: 64px;
  width: 64px;
  max-width: 64px;
  padding: 5px 4px !important;
}
/* spacer column keeps the last input clear of the Result divider without
   widening that one column */
.ssi-gap {
  width: 12px;
  min-width: 12px;
  padding: 0 !important;
}
/* centre the number inside the compact field, no spinner arrows */
.ssi-cell :deep(input) {
  text-align: center;
  -moz-appearance: textfield;
  appearance: textfield;
}
.ssi-cell :deep(input::-webkit-outer-spin-button),
.ssi-cell :deep(input::-webkit-inner-spin-button) {
  -webkit-appearance: none;
  margin: 0;
}
.ssi-total-h {
  border-left: 1px solid rgb(var(--v-theme-borderLight));
  padding: 0 14px 6px;
}
/* enforced set-scores note + walkover link */
.ssi-required-note {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), 0.65);
}
.ssi-link {
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
  text-decoration: underline;
}
/* the champions-tiebreak column: subtly tinted so it doesn't read as a set */
.ssi-col--tb {
  color: rgb(var(--v-theme-primary));
}
.ssi-cell--tb {
  background: rgba(var(--v-theme-primary), 0.05);
  border-radius: 6px;
}
.ssi-total {
  text-align: center;
  font-size: 20px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  border-left: 1px solid rgb(var(--v-theme-borderLight));
  width: 64px;
  padding: 5px 14px;
}
</style>

<style>
/* The scorer selects' MENU renders in a body-level overlay, so the compact
   sizing has to be global — scoped to the menu-props contentClass. Same
   deliberate one-off exception as the field itself. */
.ssi-scorer-menu .v-list-item-title {
  font-size: 12px;
}

.ssi-scorer-menu .v-list-item {
  min-height: 32px;
}
</style>
